<template>
  <div class="month-navigator-float" :class="{ 'menu-open': menuOpen }">
    <!-- Partial Month Indicator -->
    <div v-if="isPartialMonth" class="indicator-container">
      <PartialMonthIndicator />
    </div>
    
    <!-- Floating Navigation Button -->
    <div class="nav-button">
      <button @click="previousMonth" class="nav-arrow left-arrow" title="Previous month">
        <i class="fa-solid fa-chevron-left"></i>
      </button>

      <div class="month-display">
        <div class="month-text">{{ currentMonthDisplay }}</div>
        <div v-if="!isViewingCurrentMonth" class="past-indicator">
          <i class="fa-solid fa-clock-rotate-left"></i>
        </div>
      </div>

      <button @click="nextMonth" class="nav-arrow right-arrow" :disabled="isViewingCurrentMonth" title="Next month">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { useBudgetData } from '../composables/useBudgetData';
import PartialMonthIndicator from './PartialMonthIndicator.vue';

defineProps({
  menuOpen: {
    type: Boolean,
    default: false
  }
});

const { currentMonthDisplay, isViewingCurrentMonth, previousMonth, nextMonth, isPartialMonth } = useBudgetData();
</script>

<style scoped>
.month-navigator-float {
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  z-index: 1040;
  transition: left 0.3s ease-out;
}

/* Desktop: shift right when menu opens */
@media (min-width: 769px) {
  .month-navigator-float.menu-open {
    left: calc(225px + 2rem);
  }
}

/* Mobile: no shift, overlay mode */
@media (max-width: 768px) {
  .month-navigator-float.menu-open {
    left: 2rem;
  }
}

/* Main Navigation Button */
.nav-button {
  background-color: var(--btn-primary-color);
  color: white;
  border-radius: 50px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 8px 25px color-mix(in srgb, var(--btn-primary-color) 10%, transparent);
  transition: all 0.3s ease;
  border: none;
}

.nav-button:hover {
  box-shadow: 0 8px 25px color-mix(in srgb, var(--btn-primary-color) 10%, transparent);
  transform: translateY(-2px);
}

/* Navigation Arrows */
.nav-arrow {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.nav-arrow:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.nav-arrow:active:not(:disabled) {
  transform: scale(0.95);
}

.nav-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Month Display */
.month-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.5rem;
  min-width: 140px;
  justify-content: center;
  user-select: none;
  cursor: default;
}

.month-text {
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
  text-align: center;
  user-select: none;
}

.past-indicator {
  font-size: 0.85rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  animation: pulse 2s ease-in-out infinite;
}

/* Partial Month Indicator Container */
.indicator-container {
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: center;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 0.7;
  }

  50% {
    opacity: 1;
  }
}

/* Dark Mode Support */
:global([data-theme]) .nav-button {
  background-color: #36A2EB;
  box-shadow: 0 8px 25px rgba(54, 162, 235, 0.3);
}

:global([data-theme]) .nav-button:hover {
  box-shadow: 0 12px 35px rgba(54, 162, 235, 0.4);
}

/* Desktop: Floating button at bottom-left */
@media (min-width: 1200px) {
  .month-navigator-float {
    position: fixed;
    bottom: 2rem;
    left: 2rem;
    z-index: 1040;
  }
}

/* Mobile/Tablet: Scrollable at top */
@media (max-width: 1199px) {
  .month-navigator-float {
    position: relative;
    margin-bottom: 1rem;
    width: 100%;
    left: 0 !important;
    top: 0;
    z-index: 0;
  }

  .nav-button {
    width: 100%;
    justify-content: space-between;
    padding: 1rem 1.5rem;
  }

  .month-display {
    min-width: auto;
    flex: 1;
  }
}

/* Adjust position for smaller desktop screens */
@media (max-width: 1400px) {
  .month-navigator-float {
    left: 1.5rem;
    bottom: 1.5rem;
  }
}
</style>
